import { AppearanceSet, BindingMap } from './docx-binding';
import {
  ApprovalRequirement,
  ContentKind,
  MatchReviewMode,
  PipelineMode,
  ProfileReviewMode,
  ReportBranding,
} from './report';

export type ScopeComponentDependency =
  | { type: 'scope_component'; scope_component_slug: string }
  | { type: 'all_sections' };

export type ScopeComponentDataType = 'text' | 'image';

export interface ScopeComponent {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  dependencies: ScopeComponentDependency[];
  defaultPrompt: string | null;
  dataType: ScopeComponentDataType | null;
  /**
   * True when any capability needs an external API (≥1 jurisdiction).
   * Optional on the type (additive field) but always set by the backend and
   * normalized to a concrete boolean in `transformToScopeComponent`.
   */
  hasApiDependency?: boolean;
  /**
   * Subject types this section applies to (`['company']` / `['individual']`).
   * Null/empty means it applies to both. Gates the whole section at plan-build.
   * Optional on the type (additive field), normalized to `string[] | null` in
   * `transformToScopeComponent`.
   */
  applicableSubjectTypes?: string[] | null;
  /**
   * `'narrative'` | `'data_source'` | null (null == narrative). Data-source
   * components declare a versioned output schema; they're addable to configs
   * so their values surface in the docx template-mapping catalog.
   */
  componentKind?: string | null;
  /**
   * Providers this data leaf's capabilities declare, deduped across
   * jurisdictions. Empty for sections and for leaves fulfilled by a human.
   * A report config can switch individual entries off; it can never add one.
   * Optional on the type (additive field), normalized in
   * `transformToScopeComponent`.
   */
  providers?: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

// Request types for ScopeComponent CRUD
export interface ScopeComponentCreateRequest {
  name: string;
  slug: string;
  description?: string | null;
  default_prompt?: string | null;
  data_type?: ScopeComponentDataType | null;
  dependencies?: ScopeComponentDependency[];
  applicable_subject_types?: string[] | null;
}

export interface ScopeComponentUpdateRequest {
  name?: string;
  description?: string | null;
  default_prompt?: string | null;
  data_type?: ScopeComponentDataType | null;
  dependencies?: ScopeComponentDependency[];
  applicable_subject_types?: string[] | null;
}

export interface DependencyValidationRequest {
  dependencies: ScopeComponentDependency[];
  exclude_slug?: string;
}

export interface DependencyValidationResponse {
  valid: boolean;
  errors: string[];
  resolved_components: ApiScopeComponent[];
}

export interface DeletionBlockedResponse {
  detail: string;
  referenced_by_configs: Array<{ id: string; name: string }>;
  referenced_by_components: Array<{ slug: string; name: string }>;
}

// Rendering context catalogue types
export interface SubjectMetadataField {
  name: string;
  type: string;
  description: string;
  example?: string;
  always_present: boolean;
}

export interface SubjectMetadataSchema {
  description: string;
  fields: SubjectMetadataField[];
}

export interface BackendComponentSchema {
  slug: string;
  name: string;
  description: string;
  output_schema: Record<string, unknown>;
}

export interface SelectableComponentSummary {
  slug: string;
  name: string;
  description: string | null;
  dependencies: ScopeComponentDependency[];
  output_type: 'markdown';
}

export interface PromptSection {
  name: string;
  source: string;
  description: string;
}

export interface PromptTemplateInfo {
  description: string;
  sections: PromptSection[];
}

export interface RenderingContextCatalogue {
  subject_metadata: SubjectMetadataSchema;
  backend_components: BackendComponentSchema[];
  selectable_components: SelectableComponentSummary[];
  prompt_template: PromptTemplateInfo;
}

export interface ReportConfigComponent {
  id: string;
  scopeComponentId: string;
  overridePrompt: string | null;
  settings: Record<string, unknown> | null;
  scopeComponent: ScopeComponent | null;
}

// Capability data requirements (discriminated union by `type`)

export interface DataRequirementApi {
  type: 'api';
  provider: string;
  type_of_check: string;
  scope_component_slug?: string | null;
  provider_config?: Record<string, unknown>;
}

export interface DataRequirementHuman {
  type: 'human';
  source_scope_component_slug?: string | null;
  description?: string | null;
}

export interface DataRequirementMetadata {
  type: 'metadata';
  field?: string | null;
}

export interface DataRequirementScopeComponent {
  type: 'scope_component';
}

export type DataRequirement =
  | DataRequirementApi
  | DataRequirementHuman
  | DataRequirementMetadata
  | DataRequirementScopeComponent;

export type DataRequirementType = DataRequirement['type'];

export type DimensionMode = 'all' | 'includes' | 'excludes';

export interface DimensionCondition {
  mode: DimensionMode;
  values: string[];
}

export interface ScopeComponentCapability {
  id: string;
  scopeComponent: ScopeComponent;
  dataRequirements: DataRequirement[];
  jurisdictions: DimensionCondition | null;
  subjurisdictions: DimensionCondition | null;
}

export interface ApiScopeComponentCapability {
  id: string;
  scope_component: ApiScopeComponent;
  data_requirements: DataRequirement[];
  jurisdictions: DimensionCondition | null;
  subjurisdictions: DimensionCondition | null;
}

export interface ScopeComponentCapabilityCreateRequest {
  data_requirements: DataRequirement[];
  jurisdictions?: DimensionCondition | null;
  subjurisdictions?: DimensionCondition | null;
}

export interface ScopeComponentCapabilityUpdateRequest {
  data_requirements?: DataRequirement[];
  jurisdictions?: DimensionCondition | null;
  subjurisdictions?: DimensionCondition | null;
}

export interface DataRequirementEnums {
  /** Providers that can fulfil a scope component. Excludes research-only ones. */
  providers: string[];
  type_of_checks: string[];
  /**
   * provider -> the check types it can serve. Most provider/check combinations
   * are invalid and rejected on save, so the editor picks from this rather than
   * offering the two lists independently.
   */
  provider_checks: Record<string, string[]>;
}

export type Currency = 'GBP' | 'EUR' | 'USD';
export const CURRENCY_OPTIONS: Currency[] = ['GBP', 'EUR', 'USD'];

export interface ReportConfig {
  id: string;
  /** Teams this config is applicable to; EMPTY = every team. */
  teamIds: string[];
  name: string;
  slug: string;
  description: string | null;
  /** Admin-authored programme context threaded into the screening prompts.
   * Informs required corroboration and severity reading; never identity. */
  screeningContext?: string | null;
  isDefault: boolean;
  isActive: boolean;
  /** Client visibility: when true the config appears in client
   * catalogues of every applicable team; when false it is admin-only. */
  visible: boolean;
  approvalRequired: ApprovalRequirement;
  matchReview: MatchReviewMode;
  matchReviewThreshold: number;
  /** Who confirms the social media profiles before the screening is
   * ordered; the backend always sends it. */
  profileReview: ProfileReviewMode;
  /** Rating policy (risk profile id); null = the platform default.
   * Optional only so test fixtures stay lean — the transforms always set it. */
  riskProfileId?: string | null;
  turnaroundTimeDays: number | null;
  price: number | null;
  currency: Currency;
  triageRequired: boolean;
  /** When true (and triage is required), triaged reports wait at
   * `pending_activation` for a client/admin to press Start before running. */
  requiresClientActivation: boolean;
  /** When true, this config follows the manual / Backoffice-first (docx)
   * fulfilment flow: the scope-component picker is replaced by a link to a
   * Django Backoffice Template, and triage + approval are forced on. */
  legacyFlow: boolean;
  /** Basic-screening tier: identification + deep research skipped. */
  pipelineMode: PipelineMode;
  /** Linked Django Backoffice Template id (the seam). Null when not legacy. */
  backofficeTemplateId: number | null;
  /** Cached display name of the linked template (components fetched live). */
  backofficeTemplateName: string | null;
  version: number;
  components: ReportConfigComponent[];
  template: string | null;
  templateBlocks: Record<string, unknown> | null;
  configMetadata: Record<string, unknown> | null;
  /** Report output pipeline (plan 15): markdown assembly (default) or
   * docx template fill. Snapshotted onto each report at creation. */
  contentKind: ContentKind;
  /** Cover, header and styling of the downloaded PDF report; null = the GTI
   * default. Optional only so test fixtures stay lean - the transform always
   * sets it. */
  branding?: ReportBranding | null;
  /** S3 key of the uploaded styled template (docx configs). */
  templateDocxS3Key: string | null;
  /** Sidecar binding map (wire shape, edited verbatim by the mapping UI). */
  bindingMap: BindingMap | null;
  /** Default report-level fill variables; reports override key-by-key. */
  reportMetadataDefaults: Record<string, unknown> | null;
  /** Optional named-style preset (e.g. 'gti-v1'); superseded by styleMap. */
  styleContract: string | null;
  /** Per-config style contract: block vocabulary (paragraph/bullet_item/
   * number_item) → this template's own style names. */
  styleMap: Record<string, string> | null;
  /** Named appearance sets (plan 17): value → legend exemplar bookmark,
   * referenced by a value binding's appearance_by.set. */
  appearanceSets: Record<string, AppearanceSet> | null;
  /** Risk-matrix category rules (plan 17); not authored in the mapping UI. */
  riskMatrix: unknown[] | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ReportConfigSummary {
  id: string;
  /** Teams this config is applicable to; EMPTY = every team. */
  teamIds: string[];
  name: string;
  slug: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  /** Client visibility — see ReportConfig. */
  visible: boolean;
  approvalRequired: ApprovalRequirement;
  matchReview: MatchReviewMode;
  matchReviewThreshold: number;
  /** Who confirms the social media profiles before the screening is
   * ordered; the backend always sends it. */
  profileReview: ProfileReviewMode;
  /** Rating policy (risk profile id); null = the platform default.
   * Optional only so test fixtures stay lean — the transforms always set it. */
  riskProfileId?: string | null;
  /** Programme context threaded into the screening prompts (admin prose).
   * Optional only so test fixtures stay lean — the transforms always set it. */
  screeningContext?: string | null;
  turnaroundTimeDays: number | null;
  price: number | null;
  currency: Currency;
  triageRequired: boolean;
  requiresClientActivation: boolean;
  /** Manual / Backoffice-docx fulfilment flow — admin UIs badge these. */
  legacyFlow: boolean;
  /** Basic-screening tier — admin UIs badge these too. */
  pipelineMode: PipelineMode;
  version: number;
  componentsTotal: number;
}

// API response types (snake_case)

/**
 * Wire-format price. The server stores ``Decimal`` and serialises as
 * a string in JSON; we accept ``number`` too for forwards-compat.
 * ``parsePrice`` normalises both to ``number | null``.
 */
export type ApiPrice = number | string | null;

export interface ApiScopeComponent {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  dependencies: ScopeComponentDependency[];
  /** Absent on list rows; present on the detail read. */
  default_prompt?: string | null;
  data_type: ScopeComponentDataType | null;
  has_api_dependency?: boolean;
  applicable_subject_types?: string[] | null;
  component_kind?: string | null;
  providers?: string[];
  created_at: string | null;
  updated_at: string | null;
}

export interface ApiReportConfigComponent {
  id: string;
  scope_component_id: string;
  override_prompt: string | null;
  settings: Record<string, unknown> | null;
  scope_component: ApiScopeComponent | null;
}

export interface ApiReportConfig {
  id: string;
  team_ids?: string[];
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  visible?: boolean;
  approval_required: ApprovalRequirement;
  match_review?: MatchReviewMode;
  match_review_threshold?: number;
  profile_review?: ProfileReviewMode;
  risk_profile_id?: string | null;
  screening_context?: string | null;
  turnaround_time_days?: number | null;
  // price is a Decimal on the server and serialises as a string in JSON.
  // Keep the union so transforms can handle either shape.
  price?: ApiPrice;
  currency?: Currency;
  triage_required?: boolean;
  requires_client_activation?: boolean;
  legacy_flow?: boolean;
  pipeline_mode?: PipelineMode;
  backoffice_template_id?: number | null;
  backoffice_template_name?: string | null;
  version: number;
  components: ApiReportConfigComponent[];
  template?: string | null;
  template_blocks?: Record<string, unknown> | null;
  config_metadata: Record<string, unknown> | null;
  content_kind?: ContentKind | null;
  branding?: ReportBranding | null;
  template_docx_s3_key?: string | null;
  binding_map?: BindingMap | null;
  report_metadata_defaults?: Record<string, unknown> | null;
  style_contract?: string | null;
  style_map?: Record<string, string> | null;
  appearance_sets?: Record<string, AppearanceSet> | null;
  risk_matrix?: unknown[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ApiReportConfigSummary {
  id: string;
  team_ids?: string[];
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  visible?: boolean;
  approval_required: ApprovalRequirement;
  match_review?: MatchReviewMode;
  match_review_threshold?: number;
  profile_review?: ProfileReviewMode;
  risk_profile_id?: string | null;
  screening_context?: string | null;
  turnaround_time_days?: number | null;
  price?: ApiPrice;
  currency?: Currency;
  triage_required?: boolean;
  requires_client_activation?: boolean;
  legacy_flow?: boolean;
  pipeline_mode?: PipelineMode;
  version: number;
  components_total: number;
}

// Request types
export interface ReportConfigComponentBase {
  scope_component_id: string;
  override_prompt?: string | null;
  settings?: Record<string, unknown> | null;
}

export interface ReportConfigCreateRequest {
  name: string;
  slug: string;
  team_ids?: string[];
  description?: string | null;
  is_default?: boolean;
  is_active?: boolean;
  approval_required?: ApprovalRequirement;
  match_review?: MatchReviewMode;
  match_review_threshold?: number;
  profile_review?: ProfileReviewMode;
  risk_profile_id?: string | null;
  screening_context?: string | null;
  turnaround_time_days?: number | null;
  price?: ApiPrice;
  currency?: Currency;
  triage_required?: boolean;
  requires_client_activation?: boolean;
  legacy_flow?: boolean;
  pipeline_mode?: PipelineMode;
  backoffice_template_id?: number | null;
  backoffice_template_name?: string | null;
  template?: string | null;
  template_blocks?: Record<string, unknown> | null;
  config_metadata?: Record<string, unknown> | null;
  content_kind?: ContentKind;
  /** Null = the GTI default. */
  branding?: ReportBranding | null;
  report_metadata_defaults?: Record<string, unknown> | null;
  style_contract?: string | null;
  style_map?: Record<string, string> | null;
  components?: ReportConfigComponentBase[];
  visible?: boolean;
}

export interface ReportConfigUpdateRequest {
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  is_default?: boolean | null;
  is_active?: boolean | null;
  visible?: boolean | null;
  /** Replaces the applicability set; [] = every team. Admin-only. */
  team_ids?: string[];
  approval_required?: ApprovalRequirement | null;
  match_review?: MatchReviewMode | null;
  match_review_threshold?: number | null;
  profile_review?: ProfileReviewMode | null;
  /** Explicit null clears it (back to the platform default). */
  risk_profile_id?: string | null;
  screening_context?: string | null;
  turnaround_time_days?: number | null;
  price?: ApiPrice;
  currency?: Currency;
  triage_required?: boolean;
  requires_client_activation?: boolean;
  legacy_flow?: boolean;
  pipeline_mode?: PipelineMode;
  backoffice_template_id?: number | null;
  backoffice_template_name?: string | null;
  template?: string | null;
  template_blocks?: Record<string, unknown> | null;
  config_metadata?: Record<string, unknown> | null;
  content_kind?: ContentKind;
  /** Explicit null clears it (back to the GTI default). Admin-only. */
  branding?: ReportBranding | null;
  binding_map?: BindingMap | null;
  report_metadata_defaults?: Record<string, unknown> | null;
  style_contract?: string | null;
  style_map?: Record<string, string> | null;
  appearance_sets?: Record<string, AppearanceSet> | null;
  risk_matrix?: unknown[] | null;
}

export interface ConfigExportPayloadComponent {
  scope_component_slug: string;
  override_prompt: string | null;
  settings: Record<string, unknown> | null;
}

export interface ConfigExportPayloadConfig {
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  approval_required: ApprovalRequirement;
  match_review?: MatchReviewMode;
  match_review_threshold?: number;
  profile_review?: ProfileReviewMode;
  risk_profile_id?: string | null;
  screening_context?: string | null;
  turnaround_time_days: number | null;
  price: string | null;
  currency: Currency;
  triage_required: boolean;
  requires_client_activation: boolean;
  template: string | null;
  template_blocks: Record<string, unknown> | null;
  config_metadata: Record<string, unknown> | null;
  /** PDF branding; null = the GTI default. Absent on exports made before the
   * backend carried it. */
  branding?: ReportBranding | null;
  components: ConfigExportPayloadComponent[];
}

export interface ConfigExportPayload {
  format_version: '1';
  exported_at: string;
  exported_from: string | null;
  config: ConfigExportPayloadConfig;
}

export type ImportOnConflict = 'error' | 'rename' | 'replace';

export interface ImportMissingScopeComponentsDetail {
  message: string;
  missing_scope_component_slugs: string[];
}

export interface ImportSlugConflictDetail {
  message: string;
  slug: string;
  team_ids?: string[];
}

// Transform functions
export function transformToScopeComponentCapability(
  api: ApiScopeComponentCapability
): ScopeComponentCapability {
  return {
    id: api.id,
    scopeComponent: transformToScopeComponent(api.scope_component),
    dataRequirements: api.data_requirements,
    jurisdictions: api.jurisdictions,
    subjurisdictions: api.subjurisdictions,
  };
}

export function transformToScopeComponent(api: ApiScopeComponent): ScopeComponent {
  return {
    id: api.id,
    name: api.name,
    slug: api.slug,
    description: api.description,
    dependencies: api.dependencies,
    // List rows (GET /scope-components/) omit the prompt body — it is a
    // detail-page read (GET /scope-components/{id}).
    defaultPrompt: api.default_prompt ?? null,
    dataType: api.data_type,
    hasApiDependency: api.has_api_dependency ?? false,
    applicableSubjectTypes: api.applicable_subject_types ?? null,
    componentKind: api.component_kind ?? null,
    providers: api.providers ?? [],
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformComponent(api: ApiReportConfigComponent): ReportConfigComponent {
  return {
    id: api.id,
    scopeComponentId: api.scope_component_id,
    overridePrompt: api.override_prompt,
    settings: api.settings,
    scopeComponent: api.scope_component ? transformToScopeComponent(api.scope_component) : null,
  };
}

function parsePrice(value: ApiPrice | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function transformToReportConfig(api: ApiReportConfig): ReportConfig {
  return {
    id: api.id,
    teamIds: api.team_ids ?? [],
    name: api.name,
    slug: api.slug,
    description: api.description,
    isDefault: api.is_default,
    isActive: api.is_active,
    visible: api.visible ?? true,
    approvalRequired: api.approval_required ?? 'disabled',
    matchReview: api.match_review ?? 'off',
    matchReviewThreshold: api.match_review_threshold ?? 0.9,
    profileReview: api.profile_review ?? 'off',
    riskProfileId: api.risk_profile_id ?? null,
    screeningContext: api.screening_context ?? null,
    turnaroundTimeDays: api.turnaround_time_days ?? null,
    price: parsePrice(api.price),
    currency: api.currency ?? 'USD',
    triageRequired: api.triage_required ?? false,
    requiresClientActivation: api.requires_client_activation ?? false,
    legacyFlow: api.legacy_flow ?? false,
    pipelineMode: api.pipeline_mode ?? 'full',
    backofficeTemplateId: api.backoffice_template_id ?? null,
    backofficeTemplateName: api.backoffice_template_name ?? null,
    version: api.version,
    components: (api.components ?? []).map(transformComponent),
    template: api.template ?? null,
    templateBlocks: api.template_blocks ?? null,
    configMetadata: api.config_metadata,
    contentKind: api.content_kind ?? 'markdown',
    branding: api.branding ?? null,
    templateDocxS3Key: api.template_docx_s3_key ?? null,
    bindingMap: api.binding_map ?? null,
    reportMetadataDefaults: api.report_metadata_defaults ?? null,
    styleContract: api.style_contract ?? null,
    styleMap: api.style_map ?? null,
    appearanceSets: api.appearance_sets ?? null,
    riskMatrix: api.risk_matrix ?? null,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformToReportConfigSummary(api: ApiReportConfigSummary): ReportConfigSummary {
  return {
    id: api.id,
    teamIds: api.team_ids ?? [],
    name: api.name,
    slug: api.slug,
    description: api.description,
    isDefault: api.is_default,
    isActive: api.is_active,
    visible: api.visible ?? true,
    approvalRequired: api.approval_required ?? 'disabled',
    matchReview: api.match_review ?? 'off',
    matchReviewThreshold: api.match_review_threshold ?? 0.9,
    profileReview: api.profile_review ?? 'off',
    riskProfileId: api.risk_profile_id ?? null,
    screeningContext: api.screening_context ?? null,
    turnaroundTimeDays: api.turnaround_time_days ?? null,
    price: parsePrice(api.price),
    currency: api.currency ?? 'USD',
    triageRequired: api.triage_required ?? false,
    requiresClientActivation: api.requires_client_activation ?? false,
    legacyFlow: api.legacy_flow ?? false,
    pipelineMode: api.pipeline_mode ?? 'full',
    version: api.version,
    componentsTotal: api.components_total,
  };
}

// Team type for the teams dropdown
export interface Team {
  id: number;
  name: string;
}

// --- Backoffice Templates (legacy-flow config editor + triage tile source) ---

export interface ApiBackofficeTemplateComponent {
  slug: string | null;
  name: string;
  is_automated: boolean;
  relates_to: string | null;
}

export interface ApiBackofficeTemplate {
  id: number;
  name: string;
  turn_around_time: number | null;
  organization_id: number | null;
  report_template_url: string | null;
  components: ApiBackofficeTemplateComponent[];
}

export interface BackofficeTemplateComponent {
  slug: string | null;
  name: string;
  isAutomated: boolean;
  relatesTo: string | null;
}

export interface BackofficeTemplate {
  id: number;
  name: string;
  turnAroundTime: number | null;
  organizationId: number | null;
  reportTemplateUrl: string | null;
  components: BackofficeTemplateComponent[];
}

export function transformToBackofficeTemplate(api: ApiBackofficeTemplate): BackofficeTemplate {
  return {
    id: api.id,
    name: api.name,
    turnAroundTime: api.turn_around_time ?? null,
    organizationId: api.organization_id ?? null,
    reportTemplateUrl: api.report_template_url ?? null,
    components: (api.components ?? []).map((c) => ({
      slug: c.slug,
      name: c.name,
      isAutomated: c.is_automated,
      relatesTo: c.relates_to,
    })),
  };
}

/**
 * A member of a team, from the backoffice
 * `GET /api/teams/knox/{teamId}/members/` endpoint (admin-only). Used to
 * populate the inbox "Assign to…" picker. `id` is the backoffice/Knox user id —
 * the same identity vNext stores in `assignedToUserId` (sourced from
 * `/nexus/me/`), so it can be sent straight to `POST /inbox/{id}/assign`.
 */
export interface TeamMember {
  id: string | number;
  name: string;
  email: string;
}
